import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AutoComplete, Button, Input, notification, Table, Tag, Tabs, Tooltip, Form, Select } from 'antd';
import dayjs from 'dayjs';

import { BiExport } from 'react-icons/bi';
import { FaCalendarAlt, FaEye, FaSearch } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';
import { FaBoxArchive, FaBoxOpen } from "react-icons/fa6";

import '../../../css/UserAdmin/Global.css';
import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';
import ApplicantReviewDrawer from './ApplicantReviewModal';
import ExamScheduleModal from './ExamScheduleModal';
import ExamResultModal from './ExamResultModal';
import AdmissionApprovalModal from './AdmissionApprovalModal';
import EnrollmentApprovalModal from './EnrollmentApprovalModal';

const ALL_APPLICANT_STEP_STATUS_FIELDS = [
    'registrationStatus', 'admissionRequirementsStatus', 'admissionAdminFirstStatus',
    'preferredExamAndInterviewApplicationStatus', 'admissionExamDetailsStatus',
    'approvedExamFeeStatus', 'approvedExamInterviewResult', 'examInterviewResultStatus',
    'reservationFeePaymentStepStatus', 'admissionApprovalAdminStatus', 'admissionApprovalStatus',
    'enrollmentRequirementsStatus', 'voucherApplicationStatus',
    'enrollmentApprovalAdminStatus', 'enrollmentApprovalStatus',
];

const VISIBLE_TAB_STATUS_FIELDS = [
    'admissionExamDetailsStatus',
    'approvedExamInterviewResult',
    'admissionApprovalAdminStatus',
    'enrollmentApprovalAdminStatus',
];

const getStatusLabel = (statusKey) => {
    if (!statusKey) return 'N/A';
    if (statusKey === 'approvedExamInterviewResult_custom') return 'Review Exam/Interview Result';
    return statusKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

const getStatusTagColor = (statusKey, statusValue) => {
    const lowerStatusValue = String(statusValue || '').toLowerCase();
    let color = 'default';
    if (['complete', 'paid', 'passed', 'approved', 'verified', 'active'].includes(lowerStatusValue)) color = 'green';
    else if (['submitted', 'pending verification', 'scheduled'].includes(lowerStatusValue)) color = 'blue';
    else if (['incomplete', 'required', 'pending', 'on-going'].includes(lowerStatusValue)) color = 'orange';
    else if (['rejected', 'inactive', 'dropped'].includes(lowerStatusValue)) color = 'red';
    else if (['on-waiting-list', 'waived', 'withdrawn'].includes(lowerStatusValue)) color = 'geekblue';
    return color;
};

const ApplicationsDashboard = () => {
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const [allApplicants, setAllApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);
    const [tableFilters, setTableFilters] = useState({});
    const [sorter, setSorter] = useState({});
    const [activeTabKey, setActiveTabKey] = useState('all');
    const [showArchived, setShowArchived] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });

    const [academicYears, setAcademicYears] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
    const [yearsLoading, setYearsLoading] = useState(true);

    const [academicTerms, setAcademicTerms] = useState([]);
    const [selectedAcademicTerm, setSelectedAcademicTerm] = useState('');
    const [termsLoading, setTermsLoading] = useState(false);

    const [isReviewDrawerOpen, setIsReviewDrawerOpen] = useState(false);
    const [selectedApplicantForReview, setSelectedApplicantForReview] = useState(null);

    const [isExamScheduleModalOpen, setIsExamScheduleModalOpen] = useState(false);
    const [selectedApplicantForScheduling, setSelectedApplicantForScheduling] = useState(null);
    const [examScheduleForm] = Form.useForm();

    const [isExamResultModalOpen, setIsExamResultModalOpen] = useState(false);
    const [selectedApplicantForExamResult, setSelectedApplicantForExamResult] = useState(null);

    const [isAdmissionApprovalModalOpen, setIsAdmissionApprovalModalOpen] = useState(false);
    const [selectedApplicantForApproval, setSelectedApplicantForApproval] = useState(null);

    const [isEnrollmentApprovalModalOpen, setIsEnrollmentApprovalModalOpen] = useState(false);
    const [selectedApplicantForEnrollment, setSelectedApplicantForEnrollment] = useState(null);

    const [dashboardAvailableRooms, setDashboardAvailableRooms] = useState([]);

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchAcademicYears = async () => {
            setYearsLoading(true);
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                const response = await fetch(`${apiUrl}/api/admin/years?includeInactive=true`);
                if (!response.ok) throw new Error('Failed to fetch academic years');
                const result = await response.json();
                const yearsData = result.data || [];
                setAcademicYears(yearsData);
                const activeYear = yearsData.find(y => y.status === 'Active');
                if (activeYear) {
                    setSelectedAcademicYear(activeYear.Year);
                } else if (yearsData.length > 0) {
                    setSelectedAcademicYear(yearsData[0].Year);
                }
            } catch (error) {
                notification.error({ message: 'Fetch Error', description: `Could not load academic years. ${error.message}` });
                setAcademicYears([]);
            } finally {
                setYearsLoading(false);
            }
        };
        fetchAcademicYears();
    }, []);
    
    const fetchAcademicTerms = useCallback(async (year) => {
        if (!year) return;
        setTermsLoading(true);
        setAcademicTerms([]); // Clear previous terms
        setSelectedAcademicTerm(''); // Reset selected term
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const response = await fetch(`${apiUrl}/api/admin/terms?academicYear=${encodeURIComponent(year)}&includeInactive=true`);
            if (!response.ok) throw new Error('Failed to fetch academic terms');
            const result = await response.json();
            const termsData = result.data || [];
            setAcademicTerms(termsData);
            const activeTerm = termsData.find(t => t.status === 'Active');
            if (activeTerm) {
                setSelectedAcademicTerm(activeTerm.term);
            } else if (termsData.length > 0) {
                setSelectedAcademicTerm(termsData[0].term);
            }
        } catch (error) {
            notification.error({ message: 'Fetch Error', description: `Could not load academic terms for ${year}.` });
            setAcademicTerms([]);
        } finally {
            setTermsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedAcademicYear) {
            fetchAcademicTerms(selectedAcademicYear);
        }
    }, [selectedAcademicYear, fetchAcademicTerms]);

    useEffect(() => {
        const fetchRoomsForDashboard = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                const response = await fetch(`${apiUrl}/api/admin/rooms`);
                if (!response.ok) throw new Error(`Failed to fetch rooms`);
                const result = await response.json();
                if (result.success && Array.isArray(result.data)) {
                    setDashboardAvailableRooms(result.data);
                }
            } catch (error) {
                console.error("Dashboard: Error fetching rooms:", error);
            }
        };
        fetchRoomsForDashboard();
    }, []);

    const fetchApplicants = useCallback(async (page = 1, pageSize = 50) => {
        if (yearsLoading || termsLoading || !selectedAcademicYear || !selectedAcademicTerm) {
            setAllApplicants([]);
            setLoading(false);
            if (!yearsLoading && !termsLoading) setPagination(prev => ({ ...prev, total: 0, current: 1 }));
            return;
        }

        setLoading(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const fullUrl = `${apiUrl}/api/admin/applicants/all-applicants?page=${page}&limit=${pageSize}&academicYear=${encodeURIComponent(selectedAcademicYear)}&academicTerm=${encodeURIComponent(selectedAcademicTerm)}&archived=${showArchived}`;
            const response = await fetch(fullUrl);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            if (!result.success || !Array.isArray(result.data)) {
                throw new Error(`API response issue: ${result.message || 'Data is not an array'}`);
            }
            const applicantData = result.data.map(item => ({ ...item, key: item._id }));
            setAllApplicants(applicantData);
            if (result.pagination) {
                setPagination(prev => ({
                    ...prev,
                    current: parseInt(result.pagination.page, 10),
                    pageSize: parseInt(result.pagination.limit, 10),
                    total: parseInt(result.pagination.totalDocs, 10),
                }));
            }
        } catch (error) {
            console.error('Error fetching applicants:', error);
            setAllApplicants([]);
            notification.error({ message: 'Fetch Error', description: `Failed to fetch applicant records. ${error.message}` });
        } finally {
            setLoading(false);
        }
    }, [selectedAcademicYear, selectedAcademicTerm, yearsLoading, termsLoading, showArchived]);
    
    useEffect(() => {
        fetchApplicants(1, pagination.pageSize);
    }, [showArchived, selectedAcademicYear, selectedAcademicTerm, fetchApplicants]);

    const handleTableChange = (newPagination, filters, newSorter) => {
        setTableFilters(filters);
        setSorter(newSorter);
        fetchApplicants(newPagination.current, newPagination.pageSize);
    };

    const handleRefresh = useCallback(() => {
        setSearchTerm(''); setTableFilters({}); setSorter({}); setAutocompleteOptions([]);
        if (!loading) fetchApplicants(1, pagination.pageSize);
    }, [loading, pagination.pageSize, fetchApplicants]);

    const getProcessedDataSource = useCallback((dataToProcess, currentTabKeyForFiltering) => {
        let processedData = [...dataToProcess];
        if (currentTabKeyForFiltering !== 'all' && (VISIBLE_TAB_STATUS_FIELDS.includes(currentTabKeyForFiltering) || currentTabKeyForFiltering === 'approvedExamInterviewResult_custom')) {
            if (currentTabKeyForFiltering === 'admissionExamDetailsStatus') {
                processedData = processedData.filter(item => String(item.preferredExamAndInterviewApplicationStatus || '').toLowerCase() === 'complete' && ['incomplete', 'pending', 'on-going'].includes(String(item[currentTabKeyForFiltering] || '').toLowerCase()));
            } else if (currentTabKeyForFiltering === 'approvedExamInterviewResult_custom') {
                processedData = processedData.filter(item => String(item.admissionExamDetailsStatus || '').toLowerCase() === 'complete' && (String(item.approvedExamFeeStatus || '').toLowerCase() === 'waived' || String(item.approvedExamFeeStatus || '').toLowerCase() === 'paid') && String(item.approvedExamInterviewResult || '').toLowerCase() === 'pending');
            } else if (currentTabKeyForFiltering === 'admissionApprovalAdminStatus') {
                processedData = processedData.filter(item => String(item.reservationFeePaymentStepStatus || '').toLowerCase() === 'complete' && ['incomplete', 'pending', 'on-going'].includes(String(item.admissionApprovalAdminStatus || '').toLowerCase()));
            } else if (currentTabKeyForFiltering === 'enrollmentApprovalAdminStatus') {
                processedData = processedData.filter(item => String(item.voucherApplicationStatus || '').toLowerCase() === 'complete' && ['incomplete', 'pending', 'on-going'].includes(String(item.enrollmentApprovalAdminStatus || '').toLowerCase()));
            } else {
                processedData = processedData.filter(item => ['incomplete', 'pending', 'on-going'].includes(String(item[currentTabKeyForFiltering] || '').toLowerCase()));
            }
        }
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            processedData = processedData.filter(item =>
                (item.firstName && String(item.firstName).toLowerCase().includes(searchLower)) ||
                (item.lastName && String(item.lastName).toLowerCase().includes(searchLower)) ||
                (item.email && String(item.email).toLowerCase().includes(searchLower)) ||
                (item.studentID && String(item.studentID).toLowerCase().includes(searchLower)) ||
                (item.applicantID && String(item.applicantID).toLowerCase().includes(searchLower))
            );
        }
        if (sorter.columnKey && sorter.order) {
            processedData.sort((a, b) => {
                let valA = a[sorter.columnKey]; let valB = b[sorter.columnKey];
                if (sorter.columnKey === 'fullName') {
                    valA = `${a.lastName || ''} ${a.firstName || ''}`.trim().toLowerCase();
                    valB = `${b.lastName || ''} ${b.firstName || ''}`.trim().toLowerCase();
                }
                const safeValA = valA ?? ''; const safeValB = valB ?? '';
                let comparison = 0;
                if (sorter.columnKey.toLowerCase().includes('date')) {
                    const dateA = dayjs(safeValA); const dateB = dayjs(safeValB);
                    comparison = (dateA.isValid() ? dateA.unix() : -Infinity) - (dateB.isValid() ? dateB.unix() : -Infinity);
                } else if (typeof safeValA === 'number' && typeof safeValB === 'number') {
                    comparison = safeValA - safeValB;
                } else {
                    comparison = String(safeValA).localeCompare(String(safeValB));
                }
                return sorter.order === 'ascend' ? comparison : -comparison;
            });
        }
        return processedData;
    }, [searchTerm, sorter]);

    const currentTableDataSource = useMemo(() => getProcessedDataSource(allApplicants, activeTabKey), [allApplicants, activeTabKey, getProcessedDataSource]);
    
    const handleSearchInputChange = (value) => setSearchTerm(value || '');
    const handleAutocompleteSuggest = (searchText) => {
        if (!searchText) { setAutocompleteOptions([]); return; }
        const lowerSearchText = searchText.toLowerCase();
        const fieldsToSearch = ['studentID', 'firstName', 'lastName', 'email', 'applicantID'];
        const suggestions = allApplicants.flatMap(item => fieldsToSearch.map(field => item[field])).filter(Boolean).map(value => String(value)).filter((value, index, self) => self.map(v => v.toLowerCase()).indexOf(value.toLowerCase()) === index).filter(value => value.toLowerCase().includes(lowerSearchText)).map(value => ({ value: value, label: value })).slice(0, 10);
        setAutocompleteOptions(suggestions);
    };

    const handleClearFilters = () => { setSearchTerm(''); setTableFilters({}); setSorter({}); setAutocompleteOptions([]); };
    const handleExport = () => { notification.info({ message: 'Export', description: 'Export logic to be implemented.' }); };

    const handleRejectInDashboard = async (applicantId, reason) => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const response = await fetch(`${apiUrl}/api/admin/applicants/${applicantId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ admissionAdminFirstStatus: 'Rejected', rejectionReason: reason }) });
            if (!response.ok) throw new Error('Failed to reject applicant');
            notification.success({ message: 'Applicant Rejected', description: `Applicant ${applicantId} has been marked as rejected.` });
            handleRefresh();
        } catch (error) {
            notification.error({ message: 'Rejection Failed', description: error.message });
        } finally {
            setLoading(false);
            setIsReviewDrawerOpen(false);
            setSelectedApplicantForReview(null);
        }
    };

    const handleStatusUpdate = async (applicantFromModal, genericStatus, additionalDataFromModal) => {
        setLoading(true);
        const applicantId = applicantFromModal._id;
        const reviewContext = applicantFromModal._reviewContext;
        let successMessageAction = '', endpointPath = '', payload = {};
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const announcerFullName = localStorage.getItem('fullName');
            const announcerUserID = localStorage.getItem('userID');
            const basePayload = { announcerFullName, announcerUserID };

            if (reviewContext === 'examInterviewResult') {
                successMessageAction = 'Exam/Interview result processing';
                endpointPath = `/api/admin/applicants/${applicantId}/process-exam-result`;
                payload = genericStatus === 'Approved' ? { ...basePayload, result: 'Approved', academicStrand: additionalDataFromModal.academicStrand } : { ...basePayload, result: 'Rejected', rejectionReason: additionalDataFromModal.admissionApprovalRejectMessage };
            } else if (reviewContext === 'admissionApproval') {
                successMessageAction = 'Admission approval processing';
                endpointPath = `/api/admin/applicants/${applicantId}/process-admission-approval`;
                payload = genericStatus === 'Approved' ? { ...basePayload, result: 'Approved' } : { ...basePayload, result: 'Rejected', rejectionReason: additionalDataFromModal.admissionApprovalRejectMessage };
            } else if (reviewContext === 'enrollmentApproval') {
                successMessageAction = 'Enrollment approval processing';
                endpointPath = `/api/admin/applicants/${applicantId}/process-enrollment-approval`;
                payload = genericStatus === 'Approved' ? { ...basePayload, result: 'Approved', preferredSection: additionalDataFromModal.assignedSection } : { ...basePayload, result: 'Rejected', rejectionReason: additionalDataFromModal.rejectionReason };
            } else {
                throw new Error(`Unhandled review context: ${reviewContext}`);
            }

            if (!payload.result) throw new Error(`Invalid status "${genericStatus}" for context "${reviewContext}".`);
            
            const response = await fetch(`${apiUrl}${endpointPath}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
                throw new Error(errorData.message || `Failed to process ${successMessageAction}`);
            }
            const result = await response.json();
            if (!result.success) throw new Error(result.message || `API error during ${successMessageAction}`);
            notification.success({ message: 'Update Successful', description: result.message || `${successMessageAction} for applicant was successful.` });

            if (reviewContext === 'examInterviewResult') setIsExamResultModalOpen(false);
            if (reviewContext === 'admissionApproval') setIsAdmissionApprovalModalOpen(false);
            if (reviewContext === 'enrollmentApproval') setIsEnrollmentApprovalModalOpen(false);
            handleRefresh();
        } catch (error) {
            notification.error({ message: 'Update Error', description: error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleExamScheduleSubmit = async (valuesFromModal) => {
        if (!selectedApplicantForScheduling?._id) {
            notification.error({ message: "Error", description: "No applicant selected for scheduling." }); return;
        }
        try {
            const { approvedDate, approvedTimeSlotId, approvedRoomId, approvedFeeAmount, approvedExamFeeStatus, slotDetails } = valuesFromModal;
            if (!approvedDate?.isValid()) {
                notification.error({ message: "Invalid Date", description: "The provided date is not valid." }); return;
            }
            if (!slotDetails) {
                notification.error({ message: "Invalid Time Slot", description: "The time slot details are missing." }); return;
            }
            let roomNameToSave = slotDetails.roomName || dashboardAvailableRooms.find(r => r._id === approvedRoomId)?.name;
            if (!roomNameToSave) throw new Error("Could not find the name for the selected room.");

            const announcerFullName = localStorage.getItem('fullName');
            const announcerUserID = localStorage.getItem('userID');
            const scheduleUpdateData = { approvedDate: approvedDate.toISOString(), approvedTime: slotDetails.startTime, approvedTimeSlotId, approvedExamRoomId: approvedRoomId, approvedExamRoom: roomNameToSave, approvedFeeAmount, approvedExamFeeStatus, admissionAdminFirstStatus: "Approved", admissionExamDetailsStatus: "Complete", announcerFullName, announcerUserID };
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const endpointPath = `/api/admin/applicants/${selectedApplicantForScheduling._id}/exam-schedule`;
            const response = await fetch(`${apiUrl}${endpointPath}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(scheduleUpdateData) });
            if (!response.ok) throw new Error((await response.json().catch(() => ({}))).message || 'Failed to update schedule');
            const result = await response.json();
            if (!result.success) throw new Error(result.message || 'API error on updating schedule');
            notification.success({ message: 'Exam Schedule Updated', description: result.message });
            setIsExamScheduleModalOpen(false);
            examScheduleForm.resetFields();
            handleRefresh();
        } catch (error) {
            notification.error({ message: 'Update Error', description: error.message || `Could not complete exam schedule submission.` });
        }
    };

    const baseColumns = useMemo(() => [
        { title: 'Student ID', dataIndex: 'studentID', key: 'studentID', fixed: 'left', sorter: true, width: 130 },
        { title: 'Applicant Name', key: 'fullName', width: 220, fixed: 'left', dataIndex: ['lastName', 'firstName'], sorter: true, render: (text, record) => (<div><div style={{ fontWeight: 'bold' }}>{`${record.firstName || ''} ${record.middleName || ''} ${record.lastName || ''}`.replace(/\s+/g, ' ').trim() || 'N/A'}</div>{record.email && (<div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>)}</div>), },
        { title: 'Academic Year', dataIndex: 'academicYear', key: 'academicYear', width: 120, sorter: true },
        { title: 'Academic Term', dataIndex: 'academicTerm', key: 'academicTerm', width: 120, sorter: true },
        { title: 'Grade Level', dataIndex: 'academicLevel', key: 'academicLevel', width: 120, sorter: true },
        { title: 'Applied Strand', dataIndex: 'academicStrand', key: 'academicStrand', width: 130, sorter: true },
        { title: 'Application Date', dataIndex: 'createdAt', key: 'createdAt', width: 150, sorter: true, render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD') : 'N/A' },
        { title: 'Enrollment Status', dataIndex: 'officialEnrolled', key: 'officialEnrolled', width: 150, sorter: true, render: (statusValue) => <Tag color={getStatusTagColor('officialEnrolled', statusValue ?? 'Incomplete')}>{statusValue ?? 'Incomplete'}</Tag> },
        { title: 'Action', key: 'action', fixed: 'right', width: 230, align: 'center', render: (_, record) => { const applicantSummaryForModals = { ...record }; return (<div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}> <Tooltip title="View applicant details" key="view"><Button icon={<FaEye />} onClick={() => { setSelectedApplicantForReview(applicantSummaryForModals); setIsReviewDrawerOpen(true); }}>Details</Button></Tooltip> {activeTabKey === 'admissionExamDetailsStatus' && !record.isArchived && <Tooltip title="Manage Exam Schedule" key="schedule"><Button icon={<FaCalendarAlt />} type="primary" ghost onClick={() => { setSelectedApplicantForScheduling(applicantSummaryForModals); setIsExamScheduleModalOpen(true); }}>Schedule</Button></Tooltip>} {activeTabKey === 'approvedExamInterviewResult_custom' && !record.isArchived && <Tooltip title="Review Exam/Interview Result" key="reviewResult"><Button type="primary" onClick={() => { setSelectedApplicantForExamResult({ ...applicantSummaryForModals, _reviewContext: 'examInterviewResult' }); setIsExamResultModalOpen(true); }}>Review Result</Button></Tooltip>} {activeTabKey === 'admissionApprovalAdminStatus' && !record.isArchived && <Tooltip title="Process Admission Approval" key="admissionApproval"><Button type="primary" onClick={() => { setSelectedApplicantForApproval({ ...applicantSummaryForModals, _reviewContext: 'admissionApproval' }); setIsAdmissionApprovalModalOpen(true); }}>Process Approval</Button></Tooltip>} {activeTabKey === 'enrollmentApprovalAdminStatus' && !record.isArchiv && <Tooltip title="Process Enrollment Approval" key="enrollmentApproval"><Button type="primary" onClick={() => { setSelectedApplicantForEnrollment({ ...applicantSummaryForModals, _reviewContext: 'enrollmentApproval' }); setIsEnrollmentApprovalModalOpen(true); }}>Process Enrollment</Button></Tooltip>}</div>);}},
    ], [activeTabKey]);

    const tabDefinitions = useMemo(() => [
        { key: 'all', label: `All Applications` },
        { key: 'admissionExamDetailsStatus', label: getStatusLabel('admissionExamDetailsStatus') },
        { key: 'approvedExamInterviewResult_custom', label: getStatusLabel('approvedExamInterviewResult_custom') },
        { key: 'admissionApprovalAdminStatus', label: getStatusLabel('admissionApprovalAdminStatus') },
        { key: 'enrollmentApprovalAdminStatus', label: getStatusLabel('enrollmentApprovalAdminStatus') },
    ], []);

    const itemsForTabsComponent = useMemo(() => tabDefinitions.map(tabDef => ({ key: tabDef.key, label: `${tabDef.label} (${getProcessedDataSource(allApplicants, tabDef.key).length})` })), [tabDefinitions, allApplicants, getProcessedDataSource]);

    const columnsForActiveTab = useMemo(() => {
        const commonSortProps = (colKey) => ({ sortOrder: sorter.columnKey === colKey ? sorter.order : null });
        const baseColsMap = new Map(baseColumns.map(col => [col.key, col]));
        const getColWithSorterApplied = (key) => baseColsMap.get(key) ? { ...baseColsMap.get(key), ...commonSortProps(key) } : null;

        let currentColumns = [];
        if (activeTabKey === 'all') {
            ['studentID', 'fullName', 'academicYear', 'academicTerm', 'academicLevel', 'academicStrand', 'createdAt'].forEach(key => { const col = getColWithSorterApplied(key); if (col) currentColumns.push(col); });
            ALL_APPLICANT_STEP_STATUS_FIELDS.forEach(statusKey => { currentColumns.push({ title: getStatusLabel(statusKey), dataIndex: statusKey, key: statusKey, width: 180, sorter: true, ...commonSortProps(statusKey), render: (val) => <Tag color={getStatusTagColor(statusKey, val)}>{String(val || 'N/A')}</Tag> }); });
            const officialEnrolledCol = getColWithSorterApplied('officialEnrolled');
            if (officialEnrolledCol) currentColumns.push({ ...officialEnrolledCol, fixed: 'right' });
            const actionCol = getColWithSorterApplied('action');
            if (actionCol) currentColumns.push(actionCol);
        } else {
            const relevantBaseColKeys = ['studentID', 'fullName', 'academicYear', 'academicLevel', 'createdAt', 'action'];
            currentColumns = baseColumns.filter(col => relevantBaseColKeys.includes(col.key)).map(col => ({ ...col, ...commonSortProps(col.key) }));
            let statusColumnForTab; const nameIndex = currentColumns.findIndex(col => col.key === 'fullName');
            const insertAtIndex = nameIndex !== -1 ? nameIndex + 1 : 1;
            if (activeTabKey === 'approvedExamInterviewResult_custom') {
                statusColumnForTab = { title: getStatusLabel('approvedExamInterviewResult'), dataIndex: 'approvedExamInterviewResult', key: 'approvedExamInterviewResult', width: 180, sorter: true, ...commonSortProps('approvedExamInterviewResult'), render: (val) => <Tag color={getStatusTagColor('approvedExamInterviewResult', val)}>{String(val || 'N/A')}</Tag> };
                const dependentStatusCols = [ { title: getStatusLabel('admissionExamDetailsStatus'), dataIndex: 'admissionExamDetailsStatus', key: 'admissionExamDetailsStatus_dep', width: 180, sorter: true, ...commonSortProps('admissionExamDetailsStatus'), render: (val) => <Tag color={getStatusTagColor('admissionExamDetailsStatus', val)}>{String(val || 'N/A')}</Tag> }, { title: getStatusLabel('approvedExamFeeStatus'), dataIndex: 'approvedExamFeeStatus', key: 'approvedExamFeeStatus_dep', width: 180, sorter: true, ...commonSortProps('approvedExamFeeStatus'), render: (val) => <Tag color={getStatusTagColor('approvedExamFeeStatus', val)}>{String(val || 'N/A')}</Tag> } ];
                currentColumns.splice(insertAtIndex, 0, statusColumnForTab, ...dependentStatusCols.reverse());
            } else if (VISIBLE_TAB_STATUS_FIELDS.includes(activeTabKey)) {
                statusColumnForTab = { title: getStatusLabel(activeTabKey), dataIndex: activeTabKey, key: activeTabKey, width: 180, sorter: true, ...commonSortProps(activeTabKey), render: (val) => <Tag color={getStatusTagColor(activeTabKey, val)}>{String(val || 'N/A')}</Tag> };
                currentColumns.splice(insertAtIndex, 0, statusColumnForTab);
            }
        }
        return currentColumns.filter(Boolean);
    }, [activeTabKey, baseColumns, sorter]);

    const scrollX = useMemo(() => columnsForActiveTab.reduce((acc, col) => acc + (col.width || 150), 0), [columnsForActiveTab]);

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            {showArchived && (<Tag className="archived-tag" color="volcano">Viewing <strong>Archived Applications</strong></Tag>)}
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container"><SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} /></div>
                <main className="admin-main-content-area">
                    <div className="page-title"><p className="heading">{showArchived ? "Archived Applications" : "Manage Applications"}</p></div>
                    <div className="table-functions" style={{ marginBottom: '16px' }}>
                        <div className="left-tools">
                            <Select
                                value={selectedAcademicYear || undefined}
                                onChange={(value) => setSelectedAcademicYear(value)}
                                style={{ width: 180, marginRight: 8 }}
                                loading={yearsLoading}
                                placeholder="Select Year"
                            >
                                {academicYears.map(year => (<Select.Option key={year._id} value={year.Year}>{year.Year} {year.status === 'Active' && '(Active)'}</Select.Option>))}
                            </Select>
                            <Select
                                value={selectedAcademicTerm || undefined}
                                onChange={(value) => setSelectedAcademicTerm(value)}
                                style={{ width: 180, marginRight: 8 }}
                                loading={termsLoading}
                                placeholder="Select Term"
                                disabled={!selectedAcademicYear || termsLoading}
                            >
                                {academicTerms.map(term => (<Select.Option key={term._id} value={term.term}>{term.term} {term.status === 'Active' && '(Active)'}</Select.Option>))}
                            </Select>
                            <Button icon={<FiFilter />} onClick={handleClearFilters}>Clear Filters</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={handleRefresh} loading={loading}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={handleExport}>Export</Button>
                        </div>
                        <div className="right-tools">
                            <Button icon={showArchived ? <FaBoxOpen /> : <FaBoxArchive />} onClick={() => setShowArchived(prev => !prev)}>
                                {showArchived ? 'Close Archive' : 'Show Archive'}
                            </Button>
                            <AutoComplete value={searchTerm} options={autocompleteOptions} style={{ width: 300 }} onSearch={handleAutocompleteSuggest} onChange={handleSearchInputChange} onSelect={handleSearchInputChange} placeholder="Global Search (Name, ID, Email...)" allowClear>
                                <Input suffix={<FaSearch style={{ color: '#aaa' }} />} />
                            </AutoComplete>
                        </div>
                    </div>
                    <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} type="card" items={itemsForTabsComponent} />
                    <div style={{ marginTop: '-1px', borderTop: '1px solid #f0f0f0' }}>
                        <Table
                            columns={columnsForActiveTab}
                            dataSource={currentTableDataSource}
                            loading={loading}
                            pagination={{...pagination, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} applicants`}}
                            onChange={handleTableChange}
                            scroll={{ x: Math.max(scrollX, 1200) }}
                            size="middle"
                            rowKey="key"
                        />
                    </div>
                </main>
            </div>
            <Footer />
            {isReviewDrawerOpen && <ApplicantReviewDrawer visible={isReviewDrawerOpen} onClose={() => { setIsReviewDrawerOpen(false); setSelectedApplicantForReview(null); }} applicant={selectedApplicantForReview} onReject={handleRejectInDashboard} />}
            {isExamScheduleModalOpen && <ExamScheduleModal visible={isExamScheduleModalOpen} onClose={() => { setIsExamScheduleModalOpen(false); setSelectedApplicantForScheduling(null); examScheduleForm.resetFields(); }} onSubmit={handleExamScheduleSubmit} currentApplicant={selectedApplicantForScheduling} form={examScheduleForm} />}
            {isExamResultModalOpen && <ExamResultModal visible={isExamResultModalOpen} onClose={() => { setIsExamResultModalOpen(false); setSelectedApplicantForExamResult(null); }} applicant={selectedApplicantForExamResult} onStatusUpdate={handleStatusUpdate} />}
            {isAdmissionApprovalModalOpen && <AdmissionApprovalModal visible={isAdmissionApprovalModalOpen} onClose={() => { setIsAdmissionApprovalModalOpen(false); setSelectedApplicantForApproval(null); }} applicant={selectedApplicantForApproval} onStatusUpdate={handleStatusUpdate} />}
            {isEnrollmentApprovalModalOpen && <EnrollmentApprovalModal visible={isEnrollmentApprovalModalOpen} onClose={() => { setIsEnrollmentApprovalModalOpen(false); setSelectedApplicantForEnrollment(null); }} applicant={selectedApplicantForEnrollment} onStatusUpdate={handleStatusUpdate} />}
        </div>
    );
};

export default ApplicationsDashboard;